"use client";

import { useEffect } from "react";
import Lenis from "lenis";

export default function SmoothScrollProvider() {
  useEffect(() => {
    if (typeof window === "undefined") return;

    // Respect reduced-motion preference
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    let lenis: Lenis | null = null;
    let gsapModule: typeof import("gsap").default | null = null;

    async function init() {
      const [{ default: gsap }, { default: ScrollTrigger }] = await Promise.all(
        [import("gsap"), import("gsap/ScrollTrigger")],
      );

      gsap.registerPlugin(ScrollTrigger);
      gsapModule = gsap;

      lenis = new Lenis({
        autoRaf: false,
        lerp: 0.055,
        duration: prefersReducedMotion ? 0 : 1.7,
        easing: (t) => 1 - Math.pow(1 - t, 5),
        smoothWheel: !prefersReducedMotion,
        wheelMultiplier: 0.7,

        prevent: (node) => {
          // Explicit opt-out if needed
          if (node.hasAttribute("data-lenis-prevent")) {
            return true;
          }

          // Automatically detect scrollable elements
          let element: HTMLElement | null = node;

          while (element) {
            const style = window.getComputedStyle(element);

            const canScrollY =
              (style.overflowY === "auto" ||
                style.overflowY === "scroll" ||
                style.overflowY === "overlay") &&
              element.scrollHeight > element.clientHeight;

            const canScrollX =
              (style.overflowX === "auto" ||
                style.overflowX === "scroll" ||
                style.overflowX === "overlay") &&
              element.scrollWidth > element.clientWidth;

            if (canScrollY || canScrollX) {
              return true;
            }

            element = element.parentElement;
          }

          return false;
        },
      });

      const update = (time: number) => {
        lenis!.raf(time * 1000);
      };
      gsap.ticker.add(update);
      gsap.ticker.lagSmoothing(0);

      // Keep ScrollTrigger scroll positions in sync with Lenis
      lenis.on("scroll", ScrollTrigger.update);

      (lenis as any).__gsapUpdate = update;
    }

    init();

    return () => {
      if (!lenis || !gsapModule) return;
      gsapModule.ticker.remove((lenis as any).__gsapUpdate);
      lenis.off("scroll", ScrollTrigger.update as any);
      lenis.destroy();
      lenis = null;
    };
  }, []);

  return null;
}
